// What Sprint Planning measures a sprint against (TPT9103, pages/sprint-planning.js). Pure (no DOM,
// no api.js) so node:test can import it without stubs — same shape as lib/sprint-lifecycle.js.
//
// The capacity is the closed sprints' rolling average (rollingVelocity() in lib/burndown-chart.js)
// unless this browser holds a velocity someone typed, which overrides it until cleared.

import { escapeText, formatValue } from './burndown-chart.js';

// A typed velocity as a whole number of points above zero, else null.
export function parseVelocityOverride(value) {
  const n = parseInt(value, 10);
  return Number.isInteger(n) && n > 0 ? n : null;
}

// { points, source: 'manual' | 'average' }, or null with neither a typed number nor any history.
export function sprintCapacity(override, average) {
  const typed = parseVelocityOverride(override);
  if (typed != null) return { points: typed, source: 'manual' };
  return average > 0 ? { points: average, source: 'average' } : null;
}

// How `committed` points sit against `capacity` points: `pct` is the bar's fill, `level` says
// 'under' below 80%, 'near' from there up to the capacity, 'over' past it.
export function capacityLoad(committed, capacity) {
  const ratio = committed / capacity;
  const level = ratio > 1 ? 'over' : ratio >= 0.8 ? 'near' : 'under';
  return { ratio, pct: Math.min(ratio * 100, 100), level };
}

const LEVEL_CLASS = { under: 'planning-capacity-green', near: 'planning-capacity-yellow', over: 'planning-capacity-red' };

// "Average of the last 3 sprints", "Average of the last batch", under the project's own nouns.
export function averageSourceText(count, singular, plural) {
  return count > 1 ? `Average of the last ${count} ${plural.toLowerCase()}` : `Average of the last ${singular.toLowerCase()}`;
}

// The over/under-commit indicator, a button that opens the velocity dialog. `averageNote` says
// where an average came from (averageSourceText()).
export function renderCapacityIndicator(committed, capacity, { averageNote = '' } = {}) {
  if (!capacity) {
    return '<button type="button" class="planning-velocity-btn" id="planning-velocity-btn">Set velocity</button>';
  }
  const { pct, level } = capacityLoad(committed, capacity.points);
  const planned = `${formatValue(committed)} / ${formatValue(capacity.points)} pts`;
  const manual = capacity.source === 'manual';
  const title = manual ? 'Your own velocity, kept in this browser. Click to change it.' : `${averageNote}. Click to set your own number.`;
  const state = { under: 'Under capacity', near: 'Near capacity', over: 'Over capacity' }[level];
  return `<div class="planning-capacity ${LEVEL_CLASS[level]}">`
    + `<button type="button" class="planning-velocity-btn" id="planning-velocity-btn" title="${escapeText(title)}" aria-label="${escapeText(`${planned}, ${state.toLowerCase()}. ${title}`)}">`
    + `${escapeText(planned)}<span class="planning-capacity-source">${manual ? 'manual' : 'avg'}</span></button>`
    + `<div class="planning-capacity-bar"><div class="planning-capacity-fill" style="width:${pct}%"></div></div></div>`;
}
